'use client';

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  Text,
  Badge,
  BadgeHttp,
  Button,
  HttpEditor,
} from '@terra/ui-design-system';
import {
  extractHostFromUrl,
  extractPathFromUrl,
} from '../hooks/useElectronProxy';
import type { InterceptedRequest } from '../store/proxy-store';

type InterceptPaneKind = 'request' | 'response';

const INTERCEPT_BUTTON_PROPS = {
  size: 'xs' as const,
  h: '7',
  minH: '7',
};

export { INTERCEPT_BUTTON_PROPS };

type InterceptPaneProps = {
  kind: InterceptPaneKind;
  items: InterceptedRequest[];
  selectedId: string | null;
  onSelect: ({ id }: { id: string }) => void;
  selectedItem: InterceptedRequest | undefined;
  editorValue: string;
  onEditorChange?: ({ value }: { value: string }) => void;
  editorReadOnly?: boolean;
  editorStatusCode?: number;
  isEdited?: boolean;
  isProcessing?: boolean;
  onForward?: () => void;
  onDrop?: () => void;
  emptyTableMessage: string;
  emptyEditorMessage: string;
  queueTableHeight?: string;
  onNavigate?: ({ direction }: { direction: 'previous' | 'next' }) => void;
};

const InterceptQueueRow = React.memo(
  ({
    item,
    displayId,
    kind,
    isSelected,
    onSelect,
  }: {
    item: InterceptedRequest;
    displayId: number;
    kind: InterceptPaneKind;
    isSelected: boolean;
    onSelect: ({ id }: { id: string }) => void;
  }) => {
    const handleClick = useCallback(() => {
      onSelect({ id: item.id });
    }, [item.id, onSelect]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect({ id: item.id });
        }
      },
      [item.id, onSelect],
    );

    const host = extractHostFromUrl(item.request.url);
    const path = extractPathFromUrl(item.request.url);
    const responseSize =
      item.request.responseBodySize ??
      item.request.length ??
      item.request.responseBody?.length ??
      0;

    return (
      <HStack
        role="row"
        aria-selected={isSelected}
        data-intercept-row-id={item.id}
        px="2"
        py="1"
        borderBottomWidth="1px"
        borderColor="border.subtle"
        gap="2"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        cursor="pointer"
        bg={isSelected ? 'bg.muted' : undefined}
        _hover={{ bg: 'bg.subtle' }}
        tabIndex={0}
        opacity={item.status === 'resumed' ? 0.6 : 1}
      >
        <Box role="cell" w="36px" flexShrink={0}>
          <Text fontSize="xs" fontFamily="mono">
            {displayId}
          </Text>
        </Box>

        {kind === 'request' ? (
          <>
            <Box role="cell" w="140px" flexShrink={0} overflow="hidden">
              <Text
                fontSize="xs"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                title={host}
              >
                {host}
              </Text>
            </Box>
            <Box role="cell" w="64px" flexShrink={0}>
              <BadgeHttp method={item.request.method} size="sm">
                {item.request.method}
              </BadgeHttp>
            </Box>
            <Box role="cell" flex="1" overflow="hidden">
              <HStack gap="2">
                <Text
                  fontSize="xs"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  title={path}
                  flex="1"
                >
                  {path}
                </Text>
                {item.status === 'resumed' && (
                  <Badge variant="emphasis" size="sm">
                    Awaiting
                  </Badge>
                )}
                {isEditedRow({ item, kind }) && (
                  <Badge variant="emphasis" size="sm">
                    Edited
                  </Badge>
                )}
              </HStack>
            </Box>
          </>
        ) : (
          <>
            <Box role="cell" flex="1" overflow="hidden">
              <Text
                fontSize="xs"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                title={item.request.url}
              >
                {item.request.method} {item.request.url}
              </Text>
            </Box>
            <Box role="cell" w="48px" flexShrink={0}>
              <Text fontSize="xs" fontFamily="mono">
                {item.request.status ?? '-'}
              </Text>
            </Box>
            <Box role="cell" w="56px" flexShrink={0}>
              <Text fontSize="xs" fontFamily="mono">
                {responseSize}
              </Text>
            </Box>
            <Box role="cell" w="56px" flexShrink={0}>
              <Text fontSize="xs" fontFamily="mono">
                -
              </Text>
            </Box>
            {isEditedRow({ item, kind }) && (
              <Box role="cell" w="52px" flexShrink={0}>
                <Badge variant="emphasis" size="sm">
                  Edited
                </Badge>
              </Box>
            )}
          </>
        )}
      </HStack>
    );
  },
);

InterceptQueueRow.displayName = 'InterceptQueueRow';

function isEditedRow({
  item,
  kind,
}: {
  item: InterceptedRequest;
  kind: InterceptPaneKind;
}): boolean {
  if (kind === 'request') {
    return (
      item.originalRequestText !== undefined &&
      item.requestText !== item.originalRequestText
    );
  }

  return (
    item.originalResponseText !== undefined &&
    item.responseText !== item.originalResponseText
  );
}

export function InterceptPane({
  kind,
  items,
  selectedId,
  onSelect,
  selectedItem,
  editorValue,
  onEditorChange,
  editorReadOnly = false,
  editorStatusCode,
  isEdited = false,
  isProcessing = false,
  onForward,
  onDrop,
  emptyTableMessage,
  emptyEditorMessage,
  queueTableHeight = '160px',
  onNavigate,
}: InterceptPaneProps) {
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedId || !tableRef.current) return;
    const row = tableRef.current.querySelector(
      `[data-intercept-row-id="${selectedId}"]`,
    );

    row?.scrollIntoView({ block: 'nearest' });
  }, [selectedId, items.length]);

  useEffect(() => {
    const table = tableRef.current;

    if (!table || !onNavigate) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        onNavigate({ direction: 'previous' });
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        onNavigate({ direction: 'next' });
      }
    };

    table.addEventListener('keydown', handleKeyDown);

    return () => table.removeEventListener('keydown', handleKeyDown);
  }, [onNavigate]);

  const canActOnSelection = useMemo(() => {
    if (!selectedItem || isProcessing) return false;
    if (selectedItem.status === 'resumed') return false;
    if (kind === 'request' && selectedItem.type !== 'request') return false;
    if (kind === 'response' && selectedItem.type !== 'response') return false;

    return true;
  }, [selectedItem, isProcessing, kind]);

  const actionBadge = useMemo(() => {
    if (!selectedItem) return null;
    if (kind === 'request') {
      return selectedItem.request.method;
    }

    return String(selectedItem.request.status ?? '');
  }, [selectedItem, kind]);

  return (
    <Box
      h="full"
      w="full"
      display="flex"
      flexDirection="column"
      minH={0}
      overflow="hidden"
    >
      <Box
        ref={tableRef}
        role="table"
        tabIndex={0}
        aria-label={
          kind === 'request' ? 'Intercepted requests' : 'Intercepted responses'
        }
        w="full"
        borderBottomWidth="1px"
        borderColor="border.default"
        h={queueTableHeight}
        overflowY="auto"
        flexShrink={0}
      >
        <HStack
          role="row"
          px="2"
          py="1"
          bg="bg.subtle"
          borderBottomWidth="1px"
          borderColor="border.default"
          gap="2"
          fontSize="xs"
          fontWeight="medium"
          color="fg.muted"
          position="sticky"
          top="0"
          zIndex="1"
        >
          <Box role="columnheader" w="36px" flexShrink={0}>
            ID
          </Box>
          {kind === 'request' ? (
            <>
              <Box role="columnheader" w="140px" flexShrink={0}>
                Host
              </Box>
              <Box role="columnheader" w="64px" flexShrink={0}>
                Method
              </Box>
              <Box role="columnheader" flex="1">
                Path
              </Box>
            </>
          ) : (
            <>
              <Box role="columnheader" flex="1">
                Request
              </Box>
              <Box role="columnheader" w="48px" flexShrink={0}>
                Sta
              </Box>
              <Box role="columnheader" w="56px" flexShrink={0}>
                Res
              </Box>
              <Box role="columnheader" w="56px" flexShrink={0}>
                ms
              </Box>
            </>
          )}
        </HStack>

        {items.length === 0 ? (
          <Box role="row" p="4" textAlign="center">
            <Text fontSize="xs" color="fg.muted">
              {emptyTableMessage}
            </Text>
          </Box>
        ) : (
          items.map((item, index) => (
            <InterceptQueueRow
              key={item.id}
              item={item}
              displayId={index + 1}
              kind={kind}
              isSelected={selectedId === item.id}
              onSelect={onSelect}
            />
          ))
        )}
      </Box>

      <HStack
        px="2"
        py="1"
        borderBottomWidth="1px"
        borderColor="border.subtle"
        bg="bg.subtle"
        gap="1.5"
        alignItems="center"
        flexShrink={0}
        minH="8"
      >
        {selectedItem && actionBadge ? (
          <>
            <Badge variant="emphasis" size="sm">
              {actionBadge}
            </Badge>
            <Text
              fontSize="xs"
              flex="1"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
              title={selectedItem.request.url}
            >
              {selectedItem.request.url}
            </Text>
            {isEdited && (
              <Badge variant="emphasis" size="sm">
                Edited
              </Badge>
            )}
            <Button
              {...INTERCEPT_BUTTON_PROPS}
              variant="outline"
              colorPalette="red"
              onClick={onDrop}
              disabled={!canActOnSelection}
            >
              Drop
            </Button>
            <Button
              {...INTERCEPT_BUTTON_PROPS}
              colorPalette="green"
              onClick={onForward}
              disabled={!canActOnSelection}
            >
              Forward
            </Button>
          </>
        ) : (
          <Text fontSize="xs" color="fg.muted">
            Select an item from the queue
          </Text>
        )}
      </HStack>

      <Box flex="1" overflow="hidden" minH={0}>
        {selectedItem ? (
          <HttpEditor
            value={editorValue}
            onChange={
              onEditorChange ? (value) => onEditorChange({ value }) : undefined
            }
            readOnly={editorReadOnly}
            statusCode={editorStatusCode}
            showViewModeToggle
            showStatusBar={kind === 'response'}
          />
        ) : (
          <Box
            h="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p="4"
          >
            <Text fontSize="xs" color="fg.muted" textAlign="center">
              {emptyEditorMessage}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
}
